import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from 'api';
import { useSelector } from 'react-redux';

import { DOMAINS, IMPORTANCE, SECTIONS } from '@repo/monitoring';

import { apiBasePath, apiName } from 'assets';
import { upsertAcknowledgment } from 'helpers/acknowledgments';
import { getMainAccountId } from 'store/account';
import { Acknowledgment, AcknowledgmentResource } from 'types/Acknowledgment';
import { getAcknowledgmentsQuery } from './useAcknowledgments';

type NewAcknowledgment = {
  tinyaccount_id: string;
  resource_type: AcknowledgmentResource;
  resource_id: string;
  kit_id: string;
};

// acknowledged_at is deliberately absent: the server stamps it
const createAcknowledgment = async (
  newAcknowledgment: NewAcknowledgment,
): Promise<Acknowledgment> => {
  const response = await API.put(
    apiName,
    `${apiBasePath}/acknowledgments`,
    { body: newAcknowledgment },
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.CONDITIONS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: { ...newAcknowledgment },
    },
  );

  return response.result;
};

const useCreateAcknowledgment = () => {
  const queryClient = useQueryClient();
  const mainAccountId = useSelector(getMainAccountId);

  return useMutation({
    mutationFn: createAcknowledgment,
    onSuccess: acknowledgment => {
      // written straight into the cache so the modal closes on the response
      queryClient.setQueryData<Acknowledgment[]>(
        getAcknowledgmentsQuery(mainAccountId).queryKey,
        previous => upsertAcknowledgment(previous ?? [], acknowledgment),
      );
    },
  });
};

export default useCreateAcknowledgment;
